import {
  UTCDate,
  appendIssueToReport,
  appendLargerCoverageToReport,
} from '../utils'

import axios from 'axios'
import { errorMessages } from './errorMessages'

const LOCAL = 'local'
const STAGING = 'dev'
const PRODUCTION = 'production'
const productionOrigin = 'https://fuzzing.diligence.tools'
const stagingOrigin = 'https://fuzzing-staging.diligence.tools'

let environment = STAGING

if (
  (window && window.location.origin === productionOrigin) ||
  process.env.REACT_APP_API_URL === productionOrigin
) {
  environment = PRODUCTION
}

export const featuresEnabled = {
  stripeSubscriptions: true,
}

const stripeRedirectUrl =
  environment === PRODUCTION ? productionOrigin : stagingOrigin

let clientEnvironment = environment

if (window.location.origin.indexOf('localhost') > -1) {
  clientEnvironment = LOCAL
}

export const ENVIRONMENT = {
  client: clientEnvironment,
  api: environment,
}

// Auth0 Config Values

export const auth0Config =
  environment === PRODUCTION
    ? {
        domain: 'diligence.us.auth0.com',
        clientId: '1w330g7STTRfENOQ8tAIpOK8KM9wfgZf',
        redirectUri: window.location.origin,
        refreshTokenRedirectUrl: window.location.origin + '/keys',
        scope:
          'read:current_user enroll read:authenticators remove:authenticators',
        audience: 'https://faas.diligence.tools/',
        connectionName: 'Username-Password-Authentication',
      }
    : {
        domain: 'diligence-staging.us.auth0.com',
        clientId: 'lwwuhs4o57VW7rYyNn9JJYtWzvIi2LLB',
        redirectUri: window.location.origin,
        refreshTokenRedirectUrl: window.location.origin + '/keys',
        scope:
          'read:current_user enroll read:authenticators remove:authenticators',
        audience: 'https://faas.staging.diligence.tools/',
        connectionName: 'Username-Password-Authentication',
      }

const baseUrl =
  process.env.REACT_APP_API_URL === undefined
    ? window.location.origin
    : process.env.REACT_APP_API_URL

const tokenAddress = `${baseUrl}/api/user/token`
const campaignsAddress = `${baseUrl}/api/campaigns`
const projectsAddress = `${baseUrl}/api/projects`
const mfaAddress = `${baseUrl}/api/user`
const usersAddress = `${baseUrl}/api/users`
const checkoutSessionAddress = `${baseUrl}/api/account/checkout-session`
const subscriptionsAddress = `${baseUrl}/api/account/subscriptions`
const customerPortalAddress = `${baseUrl}/api/account/portal-session`
const fuzzingLimitAddress = `${baseUrl}/api/account/fuzzing-limit`
const consumptionAddress = `${baseUrl}/api/account/consumption`

const reduceInput = (input) => {
  const init = {
    sources: {},
    sourceList: [],
    mainSourceFiles: [],
  }
  const reduced = input.reduce((acc, curr) => {
    acc.sources = { ...acc.sources, ...curr.sources }
    acc.sourceList = [...acc.sourceList, ...curr.sourceList]
    acc.mainSourceFiles = [...acc.mainSourceFiles, curr.mainSourceFile]
    return acc
  }, init)
  return reduced
}

// campaigns

export const startCampaign = async (id, auth0) => {
  try {
    const result = await axios.post(
      `${campaignsAddress}/${id}/start`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to start campaign')
  }
}

export const stopCampaign = async (id, auth0) => {
  try {
    const result = await axios.post(
      `${campaignsAddress}/${id}/stop`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to stop campaign')
  }
}

export const updateCampaignName = async (id, name, auth0) => {
  try {
    const result = await axios.patch(
      `${campaignsAddress}/${id}`,
      {
        name,
      },
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to update campaign')
  }
}

export const updateCampaign = async (id, name, project, auth0) => {
  try {
    const result = await axios.patch(
      `${campaignsAddress}/${id}`,
      {
        name,
        project,
      },
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to update campaign')
  }
}

export const getCampaignStatus = async (id, auth0) => {
  try {
    const result = await axios.get(
      `${campaignsAddress}/${id}`,
      auth0.isAuthenticated
        ? {
            headers: {
              Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
            },
          }
        : {}
    )
    // const result = {data: mockShareMetadata}
    return patchDate(result.data)
  } catch (e) {
    if (e.toJSON().status === 404) {
      throw new Error(errorMessages.CAMPAIGN_NOT_FOUND)
    }
    throw new Error('API Error: Unable to fetch campaign status')
  }
}

export const getCampaignInput = async (id, auth0) => {
  try {
    const result = await axios.get(
      `${campaignsAddress}/${id}/input`,
      auth0.isAuthenticated
        ? {
            headers: {
              Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
            },
          }
        : {}
    )
    // const result = {data: mockShareInput}
    return reduceInput(result.data)
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to fetch campaign input')
  }
}

export const getRawCampaignInput = async (id, auth0) => {
  try {
    const result = await axios.get(`${campaignsAddress}/${id}/input`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to fetch campaign input')
  }
}

export const getHarveyRawOutput = async (id, auth0) => {
  try {
    const result = await axios.get(`${campaignsAddress}/${id}/raw`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to fetch campaign input')
  }
}

export const getCampaignIssues = async (id, auth0) => {
  try {
    const TEST_MODE = false
    // test mode makes it that new issues are continuously incoming 
    if (TEST_MODE) {
      if (!window.counter) {
        window.counter = 0
      }

      window.issuesExample = issuesExample

      for (let i = -1; i < window.counter + 1; i++) {
        window.issuesExample = appendIssueToReport(window.issuesExample)
        window.issuesExample = appendLargerCoverageToReport(
          window.issuesExample
        )
      }
      window.counter = window.counter + 1
      return window.issuesExample
    } else {
      const result = await axios.get(
        `${campaignsAddress}/${id}/issues`,
        auth0.isAuthenticated
          ? {
              headers: {
                Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
              },
            }
          : {}
      )

      return result.data
    }
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to fetch campaign issues')
  }
}

export const getCampaignsList = async (auth0) => {
  try {
    const result = await axios.get(`${campaignsAddress}/`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data.items.map(patchDate)
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to fetch campaign list')
  }
}

export const enableShareReport = async (campaignId, auth0) => {
  try {
    const result = await axios.post(
      `${campaignsAddress}/${campaignId}/share`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to share report')
  }
}

export const disableShareReport = async (campaignId, auth0) => {
  try {
    const result = await axios.delete(
      `${campaignsAddress}/${campaignId}/share`,
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to share report')
  }
}

// Projects

export const createProject = async (projectName, auth0) => {
  try {
    const result = await axios.post(
      `${projectsAddress}/`,
      { name: projectName },
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to create project')
  }
}

export const deleteProject = async (projectId, auth0) => {
  try {
    const result = await axios.delete(`${projectsAddress}/${projectId}`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to delete project')
  }
}

export const getProjectsList = async (auth0) => {
  try {
    const result = await axios.get(`${projectsAddress}/`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data.items.map(patchProject)
  } catch (e) {
    if (e.message.indexOf('Login required') > -1) {
      console.warn('API: Not authenticated, unable to get projects list.')
    } else {
      console.error(e)
      throw new Error('API Error: Unable to get projects list')
    }
  }
}

export const getProjectById = async (projectId, auth0) => {
  try {
    const result = await axios.get(`${projectsAddress}/${projectId}`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return patchProject(result.data)
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to get project by id')
  }
}

// Auth

export const getRefreshTokensList = async (auth0) => {
  try {
    const result = await axios.get(`${tokenAddress}`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data.items
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to get API Keys list.')
  }
}

export const getRefreshToken = async ({ name, authorizationCode, auth0 }) => {
  try {
    const result = await axios.post(
      `${tokenAddress}`,
      {
        name,
        authorizationCode,
        redirectUri: auth0Config.refreshTokenRedirectUrl,
      },
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to get Refresh Token')
  }
}

export const deleteRefreshToken = async (tokenId, auth0) => {
  try {
    const result = await axios.delete(`${tokenAddress}/${tokenId}`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to delete API Key')
  }
}

export const getMFAFactorsList = async (mfaAccessToken) => {
  try {
    const result = await axios.get(
      `https://${auth0Config.domain}/mfa/authenticators/`,
      {
        headers: {
          Authorization: `Bearer ${mfaAccessToken}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to get MFA factors list.')
  }
}

export const deleteMFAFactor = async (mfaId, mfaAccessToken) => {
  try {
    const result = await axios.delete(
      `https://${auth0Config.domain}/mfa/authenticators/${mfaId}`,
      {
        headers: {
          Authorization: `Bearer ${mfaAccessToken}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to delete MFA factor ', e)
  }
}

export const getLinkToSetUpMFA = async (auth0) => {
  try {
    const result = await axios.post(
      `${mfaAddress}/enable_mfa`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to set up MFA')
  }
}

export const sendResetPasswordEmail = async (email) => {
  try {
    const result = await axios.post(
      `https://${auth0Config.domain}/dbconnections/change_password`,
      {
        email,
        client_id: auth0Config.clientId,
        connection: auth0Config.connectionName,
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to send password reset email.')
  }
}

/**
 *
 * Projects
 */

const patchProject = (project) => {
  if (project.createdAt) {
    project.createdAt = patchDate(project.createdAt)
  }
  if (project.campaigns) {
    project.campaigns = project.campaigns.map(patchDate)
  }
  return project
}

const patchDate = (item) => {
  if (item.submittedAt) {
    item.submittedAt = UTCDate(item.submittedAt)
  }
  if (item.startedAt) {
    item.startedAt = UTCDate(item.startedAt)
  }
  if (item.stoppedAt) {
    item.stoppedAt = UTCDate(item.stoppedAt)
  }

  return item
}

export const getRawReport = async (id, auth0) => {
  try {
    const rawInput = await getRawCampaignInput(id, auth0)
    const harveyRawOutput = await getHarveyRawOutput(id, auth0)
    return {
      requests: rawInput,
      result: harveyRawOutput,
    }
  } catch (e) {
    console.error('Error generating raw report, ', e)
    return {}
  }
}

export const getAuth0AuthorizationCodeLink = () => {
  if (environment === PRODUCTION) {
    return `https://diligence.us.auth0.com/authorize?audience=${auth0Config.audience}&scope=offline_access&response_type=code&client_id=${auth0Config.clientId}&redirect_uri=${auth0Config.refreshTokenRedirectUrl}`
  } else {
    return `https://diligence-staging.us.auth0.com/authorize?audience=${auth0Config.audience}&scope=offline_access&response_type=code&client_id=${auth0Config.clientId}&redirect_uri=${auth0Config.refreshTokenRedirectUrl}`
  }
}

// Admin
export const getAdminActiveCampaigns = async (auth0) => {
  try {
    const result = await axios.get(`${usersAddress}/campaigns?status=running`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    if (result?.data?.items) {
      return result.data.items.map(patchDate)
    } else {
      throw new Error('API Error: No users returned.', result)
    }
  } catch (e) {
    console.warn('Unable to get running scans list.')
  }
}

export const getUsers = async (auth0) => {
  try {
    const result = await axios.get(usersAddress + '/', {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    if (result?.data?.users) {
      return result.data.users
    } else {
      throw new Error('API Error: No users returned.', result)
    }
  } catch (e) {
    console.warn('Unable to get users list.')
  }
}

export const getUserMonthsUsage = async (userId, auth0) => {
  try {
    const result = await axios.get(`${usersAddress}/${userId}/metrics`, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    if (result?.data) {
      return result.data
    } else {
      throw new Error('API Error: No metrics returned for user ', userId)
    }
  } catch (e) {
    console.error(e)
    throw new Error(
      'API Error: Unable to get users list. Do you have admin permissions?'
    )
  }
}

export const getUserMonthStats = async ({ userId, month, year, auth0 }) => {
  try {
    const result = await axios.get(
      `${usersAddress}/${userId}/metrics/${year}/${month}`,
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    if (result?.data) {
      return result.data
    } else {
      throw new Error('API Error: No month metrics returned for user ', userId)
    }
  } catch (e) {
    console.error(e)
    throw new Error(
      'API Error: Unable to get users list. Do you have admin permissions?'
    )
  }
}

/**
 * Stripe Subscriptions
 */

export const createCheckoutSession = async (priceId, auth0) => {
  try {
    const result = await axios.post(
      `${checkoutSessionAddress}?price_id=${priceId}&success_url=${stripeRedirectUrl}&cancel_url=${stripeRedirectUrl}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to generate checkout link')
  }
}

export const createCustomerPortalSession = async (auth0) => {
  try {
    const result = await axios.post(
      `${customerPortalAddress}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to generate customer portal link')
  }
}

export const getSubscriptions = async (auth0) => {
  try {
    const result = await axios.get(subscriptionsAddress, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.warn('Unable to get subscriptions.')
  }
}

export const getConsumption = async (auth0) => {
  try {
    const result = await axios.get(consumptionAddress, {
      headers: {
        Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
      },
    })
    return result.data
  } catch (e) {
    console.warn('Unable to get consumption data.')
  }
}

export const setFuzzingHoursLimit = async (fuzzingLimitSeconds, auth0) => {
  try {
    const result = await axios.post(
      `${fuzzingLimitAddress}`,
      { fuzzingLimit: fuzzingLimitSeconds },
      {
        headers: {
          Authorization: `Bearer ${await auth0.getAccessTokenSilently()}`,
        },
      }
    )
    return result.data
  } catch (e) {
    console.error(e)
    throw new Error('API Error: Unable to updating fuzzing hours')
  }
}

const issuesExample = [
  {
    issues: [
      {
        uuid: '59cd6ac8-7db3-5e01-8aa7-0860438f7906',
        swcID: 'SWC-110',
        swcTitle: 'Assert Violation',
        description: {
          head: 'A user-provided assertion failed.',
          tail: "A user-provided assertion failed with the message '0: balances are in sync'.",
        },
        severity: 'Low',
        locations: [
          {
            sourceMap: '3903:42:3',
            sourceType: 'solidity-file',
            sourceFormat: 'text',
            sourceList: [
              '',
              '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/__scribble_ReentrancyUtils.sol',
              '',
              '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol',
            ],
          },
        ],
        extra: {
          discoveryTime: 180838494811,
          testCases: [
            {
              initialState: {
                accounts: {
                  '0x0000000000000000000000000000000000000000': {
                    nonce: 0,
                    balance:
                      '0x000000000000000000000000000000000000000000000000000a9e765ebed200',
                    code: '',
                    storage: {},
                  },
                  '0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000fffffffffffff56189a1412dff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa0': {
                    nonce: 0,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1': {
                    nonce: 0,
                    balance:
                      '0x0000000000000000000000000000000000000000000000000000000000000000',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa2': {
                    nonce: 0,
                    balance:
                      '0x0000000000000000000000000000000000ffffffffffffffffffffffffffffff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa3': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x00',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa4': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0xfd',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa5': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x608060405260005600a165627a7a72305820466f8a1bdae15c60b8e998fe04836ef505803cfbd8edd29bd4679531357576530029',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa6': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x608060405273aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa63081146038578073ffffffffffffffffffffffffffffffffffffffff16ff5b5000fea165627a7a723058205e8b906b72ad42c69b05acf4542283b6080ae82562bc74baac467daac2fb0e0e0029',
                    storage: {},
                  },
                  '0xe78a0f7e598cc8b0bb87894b0f60dd2a88d6a8ab': {
                    nonce: 1,
                    balance:
                      '0x0000000000000000000000000000000000000000000000000000000000000000',
                    code: '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',
                    storage: {
                      '0x0000000000000000000000000000000000000000000000000000000000000000':
                        '0x0000000000000000000000000000000000000000000000000000000000000001',
                      '0x0000000000000000000000000000000000000000000000000000000000000001':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                      '0x0000000000000000000000000000000000000000000000000000000000000003':
                        '0x0000000000000000000000000000000000000000000000000000000000000002',
                      '0x0000000000000000000000000000000000000000000000000000000000000005':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                      '0xaccdee18023550db6934125b989726b403f859182b9c551ed8c7b4e686303ae1':
                        '0x0000000000000000000000000000000000000000000000000000000000000001',
                      '0xc2575a0e9e593c00f959f8c92f12db2869c3395a3b0502d05e2516446f71f85c':
                        '0x00000000000000000000000090f8bf6a479f320ead074411a4b0e7944ea8c9c1',
                      '0xc30df1b2c125b5153defa9414be9c4af8ad07fe9601dbf6e43a237213263cbf9':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                    },
                  },
                },
              },
              steps: [
                {
                  address: '0xe78a0f7e598cc8b0bb87894b0f60dd2a88d6a8ab',
                  gasLimit: '0xff000',
                  gasPrice: '0x156dd3d6d',
                  input:
                    '0xa9059cbb00000000000000000000000090f8bf6a479f320ead074411a4b0e7944ea8c9c100000000000000000000000000000000000000000000000000000000000f3dfa0000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000',
                  origin: '0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1',
                  value: '0x0',
                  blockCoinbase: '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa0',
                  blockDifficulty: '0x0',
                  blockGasLimit: '0xff0000',
                  blockNumber: '0x54',
                  blockTime: '0x63efbe59',
                  decodedInput:
                    'transfer(0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1, 998906)',
                  name: 'transfer(address,uint256)',
                },
              ],
            },
          ],
          harveyId: 20,
          debugInfo: {
            0: {
              stackTrace:
                '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:91:17 VulnerableToken.__scribble_VulnerableToken_check_state_invariants_internal()\n/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:98:8 VulnerableToken.__scribble_check_state_invariants()\n/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:40:8 VulnerableToken.transfer(0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1, 998906)',
              debugVariables: {
                _totalSupply: '1000000',
              },
            },
          },
        },
        decodedLocations: [
          [
            {
              line: 91,
              column: 18,
            },
            {
              line: 91,
              column: 60,
            },
            false,
          ],
        ],
      },
      {
        uuid: '29fde185-5ede-5703-9fe4-d01c632ea1d6',
        swcID: 'SWC-110',
        swcTitle: 'Assert Violation',
        description: {
          head: 'An assertion violation was triggered.',
          tail: 'It is possible to cause an assertion violation. Note that Solidity assert() statements should only be used to check invariants. Review the transaction trace generated for this issue and either make sure your program logic is correct, or use require() instead of assert() if your goal is to constrain user inputs or enforce preconditions. Remember to validate inputs from both callers (for instance, via passed arguments) and callees (for instance, via return values).',
        },
        severity: 'Low',
        locations: [
          {
            sourceMap: '3959:13:3',
            sourceType: 'solidity-file',
            sourceFormat: 'text',
            sourceList: [
              '',
              '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/__scribble_ReentrancyUtils.sol',
              '',
              '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol',
            ],
          },
        ],
        extra: {
          discoveryTime: 180838494811,
          testCases: [
            {
              initialState: {
                accounts: {
                  '0x0000000000000000000000000000000000000000': {
                    nonce: 0,
                    balance:
                      '0x000000000000000000000000000000000000000000000000000a9e765ebed200',
                    code: '',
                    storage: {},
                  },
                  '0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000fffffffffffff56189a1412dff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa0': {
                    nonce: 0,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1': {
                    nonce: 0,
                    balance:
                      '0x0000000000000000000000000000000000000000000000000000000000000000',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa2': {
                    nonce: 0,
                    balance:
                      '0x0000000000000000000000000000000000ffffffffffffffffffffffffffffff',
                    code: '',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa3': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x00',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa4': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0xfd',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa5': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x608060405260005600a165627a7a72305820466f8a1bdae15c60b8e998fe04836ef505803cfbd8edd29bd4679531357576530029',
                    storage: {},
                  },
                  '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa6': {
                    nonce: 1,
                    balance:
                      '0x00000000000000000000000000000000000000ffffffffffffffffffffffffff',
                    code: '0x608060405273aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa63081146038578073ffffffffffffffffffffffffffffffffffffffff16ff5b5000fea165627a7a723058205e8b906b72ad42c69b05acf4542283b6080ae82562bc74baac467daac2fb0e0e0029',
                    storage: {},
                  },
                  '0xe78a0f7e598cc8b0bb87894b0f60dd2a88d6a8ab': {
                    nonce: 1,
                    balance:
                      '0x0000000000000000000000000000000000000000000000000000000000000000',
                    code: '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',
                    storage: {
                      '0x0000000000000000000000000000000000000000000000000000000000000000':
                        '0x0000000000000000000000000000000000000000000000000000000000000001',
                      '0x0000000000000000000000000000000000000000000000000000000000000001':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                      '0x0000000000000000000000000000000000000000000000000000000000000003':
                        '0x0000000000000000000000000000000000000000000000000000000000000002',
                      '0x0000000000000000000000000000000000000000000000000000000000000005':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                      '0xaccdee18023550db6934125b989726b403f859182b9c551ed8c7b4e686303ae1':
                        '0x0000000000000000000000000000000000000000000000000000000000000001',
                      '0xc2575a0e9e593c00f959f8c92f12db2869c3395a3b0502d05e2516446f71f85c':
                        '0x00000000000000000000000090f8bf6a479f320ead074411a4b0e7944ea8c9c1',
                      '0xc30df1b2c125b5153defa9414be9c4af8ad07fe9601dbf6e43a237213263cbf9':
                        '0x00000000000000000000000000000000000000000000000000000000000f4240',
                    },
                  },
                },
              },
              steps: [
                {
                  address: '0xe78a0f7e598cc8b0bb87894b0f60dd2a88d6a8ab',
                  gasLimit: '0xff000',
                  gasPrice: '0x156dd3d6d',
                  input:
                    '0xa9059cbb00000000000000000000000090f8bf6a479f320ead074411a4b0e7944ea8c9c100000000000000000000000000000000000000000000000000000000000f3dfa0000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000',
                  origin: '0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1',
                  value: '0x0',
                  blockCoinbase: '0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa0',
                  blockDifficulty: '0x0',
                  blockGasLimit: '0xff0000',
                  blockNumber: '0x54',
                  blockTime: '0x63efbe59',
                  decodedInput:
                    'transfer(0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1, 998906)',
                  name: 'transfer(address,uint256)',
                },
              ],
            },
          ],
          harveyId: 21,
          debugInfo: {
            0: {
              stackTrace:
                '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:92:12 VulnerableToken.__scribble_VulnerableToken_check_state_invariants_internal()\n/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:98:8 VulnerableToken.__scribble_check_state_invariants()\n/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol:40:8 VulnerableToken.transfer(0x90f8bf6a479f320ead074411a4b0e7944ea8c9c1, 998906)',
            },
          },
        },
        decodedLocations: [
          [
            {
              line: 92,
              column: 13,
            },
            {
              line: 92,
              column: 26,
            },
            false,
          ],
        ],
      },
    ],
    sourceType: 'raw-bytecode',
    sourceFormat: 'evm-byzantium-bytecode',
    sourceList: [
      '0xf87d6b9e74d5ba7b752cecd18cabcdf1b39299afdda52b5a3d1bd5e155af6bd8',
    ],
    meta: {
      individualCoverage: [
        {
          fileName:
            '/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol',
          contractName: 'VulnerableToken',
          instructionCoverage: 1465,
          instructionCoveragePercentage: 69,
          branchCoverage: 93,
        },
      ],
      InstructionCoverageOverTime:
        '[[0, 0], [5996538176, 1309], [11993076352, 1309], [17989614528, 1309], [23986152704, 1309], [29982690880, 1309], [35979229056, 1309], [41975767232, 1309], [47972305408, 1309], [53968843584, 1309], [59965381760, 1309], [65961919936, 1309], [71958458112, 1309], [77954996288, 1309], [83951534464, 1309], [89948072640, 1309], [95944610816, 1309], [101941148992, 1309], [107937687168, 1309], [113934225344, 1309], [119930763520, 1309], [125927301696, 1309], [131923839872, 1309], [137920378048, 1309], [143916916224, 1309], [149913454400, 1309], [155909992576, 1309], [161906530752, 1309], [167903068928, 1309], [173899607104, 1309], [179896145280, 1465], [185892683456, 1465], [191889221632, 1465], [197885759808, 1465], [203882297984, 1465], [209878836160, 1465], [215875374336, 1465], [221871912512, 1465], [227868450688, 1465], [233864988864, 1465], [239861527040, 1465], [245858065216, 1465], [251854603392, 1465], [257851141568, 1465], [263847679744, 1465], [269844217920, 1465], [275840756096, 1465], [281837294272, 1465], [287833832448, 1465], [293830370624, 1465], [299826908800, 1465], [305823446976, 1465], [311819985152, 1465], [317816523328, 1465], [323813061504, 1465], [329809599680, 1465], [335806137856, 1465], [341802676032, 1465], [347799214208, 1465], [353795752384, 1465], [359792290560, 1465], [365788828736, 1465], [371785366912, 1465], [377781905088, 1465], [383778443264, 1465], [389774981440, 1465], [395771519616, 1465], [401768057792, 1465], [407764595968, 1465], [413761134144, 1465], [419757672320, 1465], [425754210496, 1465], [431750748672, 1465], [437747286848, 1465], [443743825024, 1465], [449740363200, 1465], [455736901376, 1465], [461733439552, 1465], [467729977728, 1465], [473726515904, 1465], [479723054080, 1465], [485719592256, 1465], [491716130432, 1465], [497712668608, 1465], [503709206784, 1465], [509705744960, 1465], [515702283136, 1465], [521698821312, 1465], [527695359488, 1465], [533691897664, 1465], [539688435840, 1465], [545684974016, 1465], [551681512192, 1465], [557678050368, 1465], [563674588544, 1465], [569671126720, 1465], [575667664896, 1465], [581664203072, 1465], [587660741248, 1465], [593657279424, 1465], [599653817600, 1465], [605650355776, 1465]]',
      PercentageCoverageOverTime:
        '[[0, 0], [5996538176, 61], [11993076352, 61], [17989614528, 61], [23986152704, 61], [29982690880, 61], [35979229056, 61], [41975767232, 61], [47972305408, 61], [53968843584, 61], [59965381760, 61], [65961919936, 61], [71958458112, 61], [77954996288, 61], [83951534464, 61], [89948072640, 61], [95944610816, 61], [101941148992, 61], [107937687168, 61], [113934225344, 61], [119930763520, 61], [125927301696, 61], [131923839872, 61], [137920378048, 61], [143916916224, 61], [149913454400, 61], [155909992576, 61], [161906530752, 61], [167903068928, 61], [173899607104, 61], [179896145280, 69], [185892683456, 69], [191889221632, 69], [197885759808, 69], [203882297984, 69], [209878836160, 69], [215875374336, 69], [221871912512, 69], [227868450688, 69], [233864988864, 69], [239861527040, 69], [245858065216, 69], [251854603392, 69], [257851141568, 69], [263847679744, 69], [269844217920, 69], [275840756096, 69], [281837294272, 69], [287833832448, 69], [293830370624, 69], [299826908800, 69], [305823446976, 69], [311819985152, 69], [317816523328, 69], [323813061504, 69], [329809599680, 69], [335806137856, 69], [341802676032, 69], [347799214208, 69], [353795752384, 69], [359792290560, 69], [365788828736, 69], [371785366912, 69], [377781905088, 69], [383778443264, 69], [389774981440, 69], [395771519616, 69], [401768057792, 69], [407764595968, 69], [413761134144, 69], [419757672320, 69], [425754210496, 69], [431750748672, 69], [437747286848, 69], [443743825024, 69], [449740363200, 69], [455736901376, 69], [461733439552, 69], [467729977728, 69], [473726515904, 69], [479723054080, 69], [485719592256, 69], [491716130432, 69], [497712668608, 69], [503709206784, 69], [509705744960, 69], [515702283136, 69], [521698821312, 69], [527695359488, 69], [533691897664, 69], [539688435840, 69], [545684974016, 69], [551681512192, 69], [557678050368, 69], [563674588544, 69], [569671126720, 69], [575667664896, 69], [581664203072, 69], [587660741248, 69], [593657279424, 69], [599653817600, 69], [605650355776, 69]]',
      BranchCoverageOverTime:
        '[[0, 0], [5996538176, 86], [11993076352, 86], [17989614528, 86], [23986152704, 86], [29982690880, 86], [35979229056, 86], [41975767232, 86], [47972305408, 86], [53968843584, 86], [59965381760, 86], [65961919936, 86], [71958458112, 86], [77954996288, 86], [83951534464, 86], [89948072640, 86], [95944610816, 86], [101941148992, 86], [107937687168, 86], [113934225344, 86], [119930763520, 86], [125927301696, 86], [131923839872, 86], [137920378048, 86], [143916916224, 86], [149913454400, 86], [155909992576, 86], [161906530752, 86], [167903068928, 86], [173899607104, 86], [179896145280, 93], [185892683456, 93], [191889221632, 93], [197885759808, 93], [203882297984, 93], [209878836160, 93], [215875374336, 93], [221871912512, 93], [227868450688, 93], [233864988864, 93], [239861527040, 93], [245858065216, 93], [251854603392, 93], [257851141568, 93], [263847679744, 93], [269844217920, 93], [275840756096, 93], [281837294272, 93], [287833832448, 93], [293830370624, 93], [299826908800, 93], [305823446976, 93], [311819985152, 93], [317816523328, 93], [323813061504, 93], [329809599680, 93], [335806137856, 93], [341802676032, 93], [347799214208, 93], [353795752384, 93], [359792290560, 93], [365788828736, 93], [371785366912, 93], [377781905088, 93], [383778443264, 93], [389774981440, 93], [395771519616, 93], [401768057792, 93], [407764595968, 93], [413761134144, 93], [419757672320, 93], [425754210496, 93], [431750748672, 93], [437747286848, 93], [443743825024, 93], [449740363200, 93], [455736901376, 93], [461733439552, 93], [467729977728, 93], [473726515904, 93], [479723054080, 93], [485719592256, 93], [491716130432, 93], [497712668608, 93], [503709206784, 93], [509705744960, 93], [515702283136, 93], [521698821312, 93], [527695359488, 93], [533691897664, 93], [539688435840, 93], [545684974016, 93], [551681512192, 93], [557678050368, 93], [563674588544, 93], [569671126720, 93], [575667664896, 93], [581664203072, 93], [587660741248, 93], [593657279424, 93], [599653817600, 93], [605650355776, 93]]',
      PathCoverageOverTime:
        '[[0, 0], [5996538176, 20], [11993076352, 20], [17989614528, 20], [23986152704, 20], [29982690880, 20], [35979229056, 20], [41975767232, 20], [47972305408, 20], [53968843584, 20], [59965381760, 20], [65961919936, 20], [71958458112, 20], [77954996288, 20], [83951534464, 20], [89948072640, 20], [95944610816, 20], [101941148992, 20], [107937687168, 20], [113934225344, 20], [119930763520, 20], [125927301696, 20], [131923839872, 20], [137920378048, 20], [143916916224, 20], [149913454400, 20], [155909992576, 20], [161906530752, 20], [167903068928, 20], [173899607104, 20], [179896145280, 21], [185892683456, 21], [191889221632, 21], [197885759808, 21], [203882297984, 21], [209878836160, 21], [215875374336, 21], [221871912512, 21], [227868450688, 21], [233864988864, 21], [239861527040, 21], [245858065216, 21], [251854603392, 21], [257851141568, 21], [263847679744, 21], [269844217920, 21], [275840756096, 21], [281837294272, 21], [287833832448, 21], [293830370624, 21], [299826908800, 21], [305823446976, 21], [311819985152, 21], [317816523328, 21], [323813061504, 21], [329809599680, 21], [335806137856, 21], [341802676032, 21], [347799214208, 21], [353795752384, 21], [359792290560, 21], [365788828736, 21], [371785366912, 21], [377781905088, 21], [383778443264, 21], [389774981440, 21], [395771519616, 21], [401768057792, 21], [407764595968, 21], [413761134144, 21], [419757672320, 21], [425754210496, 21], [431750748672, 21], [437747286848, 21], [443743825024, 21], [449740363200, 21], [455736901376, 21], [461733439552, 21], [467729977728, 21], [473726515904, 21], [479723054080, 21], [485719592256, 21], [491716130432, 21], [497712668608, 21], [503709206784, 21], [509705744960, 21], [515702283136, 21], [521698821312, 21], [527695359488, 21], [533691897664, 21], [539688435840, 21], [545684974016, 21], [551681512192, 21], [557678050368, 21], [563674588544, 21], [569671126720, 21], [575667664896, 21], [581664203072, 21], [587660741248, 21], [593657279424, 21], [599653817600, 21], [605650355776, 21]]',
      LineCoverage:
        '{"/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/__scribble_ReentrancyUtils.sol": {"23": 154167300, "24": 154167300, "25": 154167300, "28": 154167300, "29": 154167300, "45": 154167300, "46": 154167300, "47": 154167300, "48": 154167300, "49": 154167300, "50": 154167300, "53": 12548600, "54": 12548600}, "/Users/boss/git/mythx/test-projects/scribble-exercise-1/contracts/vulnerableERC20.sol": {"25": 59722300, "26": 59722300, "29": 12548600, "30": 12548600, "33": 51556600, "34": 51556600, "37": 229972700, "38": 229972700, "39": 229972700, "40": 229972700, "41": 229972700, "44": 229972700, "45": 229972700, "46": 229972700, "47": 229972700, "48": 229972700, "49": 229972700, "50": 229972700, "51": 229972700, "52": 229972700, "55": 121505000, "56": 121505000, "57": 121505000, "58": 121505000, "59": 121505000, "62": 121505000, "63": 121505000, "64": 121505000, "65": 121505000, "66": 121505000, "69": 154167300, "70": 154167300, "71": 154167300, "72": 154167300, "73": 154167300, "76": 154167300, "77": 154167300, "78": 154167300, "79": 154167300, "80": 154167300, "81": 154167300, "82": 154167300, "83": 154167300, "84": 154167300, "89": 121505000, "90": 179794088697, "91": 179794088697, "92": 179794088697, "98": 121505000}}',
      AnalysisDuration: 599653817614,
      TotalBranchCoverage: 93,
      TotalInstructionCoverage: 1465,
      TotalPercentageCoverage: 69,
      coveredPaths: 21,
      residualRiskEstimate: 1.0544107583638496e-6,
      executionsPerSecond: 1581.570853286031,
      totalTestcasesExecuted: 948395,
      transactionStatus: {
        failing: 11,
        successful: 10,
        outOfGas: 0,
      },
      transactionDepth: {
        1: 19,
        2: 2,
      },
      Properties: [
        {
          id: 0,
          name: 'balances are in sync',
          sourceFile: 'contracts/vulnerableERC20.sol',
          annotationSource: '30:74:0',
          contractName: 'VulnerableToken',
          covered: true,
          violated: true,
          correlatedIssues: [
            '59cd6ac8-7db3-5e01-8aa7-0860438f7906',
            '29fde185-5ede-5703-9fe4-d01c632ea1d6',
          ],
          location: [1, 31, 2, 27],
        },
      ],
    },
  },
]
